// Snow
// Features:
// - GPU-driven snow particle system (single draw call)
// - Custom shader for particle rendering and exclusion zones
// - CPU-based physics simulation for movement and wrapping
// - Excludes particles from the cabin interior (using shader uniforms)

import { useRef, useMemo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'

import vertexShader from './shaders/snow/vertex.glsl'
import fragmentShader from './shaders/snow/fragment.glsl'

import snowTextureURL from '/textures/alpha_snow_bw.png'

// Configuration
const SNOW_CONFIG = {
    particleCount: 17000,
    particleSize: 0.12,
    moveSpeed: 10,
    cubeRotationX: 0.6,
    cubeRotationY: 0.1,
    cubeRotationZ: 1.0,
    cubeCenterX: -15,
    cubeCenterY: 20,
    cubeCenterZ: 0,
    speedVariation: 2.0,
    wiggleIntensity: 2.6,
    wiggleSpeed: 3,
    box1Min: new THREE.Vector3(-3.3, -0.3, -4.25),
    box1Max: new THREE.Vector3(3.2, 6.88, 4.18),
}

export default function Snow() {
    const meshRef = useRef()
    const particleSpeedsRef = useRef([])
    const { size, gl, camera } = useThree()

    // Textures
    const [snowTexture] = useTexture([snowTextureURL], (textures) =>
        textures.forEach((t) => {
            t.colorSpace = THREE.NoColorSpace
            t.flipY = false
        })
    )

    // Material
    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                uParticleSize: { value: SNOW_CONFIG.particleSize },
                uMap: { value: snowTexture },
                uResolution: {
                    value: new THREE.Vector2(size.width, size.height),
                },
                uProjectionScale: { value: 1.0 },
                uBox1Min: { value: SNOW_CONFIG.box1Min },
                uBox1Max: { value: SNOW_CONFIG.box1Max },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.NormalBlending,
        })
    }, [size, snowTexture])

    // Geometry
    const geometry = useMemo(() => {
        const geo = new THREE.BufferGeometry()
        const positions = new Float32Array(SNOW_CONFIG.particleCount * 3)
        particleSpeedsRef.current = []

        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
            new THREE.Euler(
                SNOW_CONFIG.cubeRotationX,
                SNOW_CONFIG.cubeRotationY,
                SNOW_CONFIG.cubeRotationZ
            )
        )

        for (let i = 0; i < SNOW_CONFIG.particleCount; i++) {
            // Local coords (-25 to +25)
            const localX = (Math.random() - 0.5) * 50
            const localY = (Math.random() - 0.5) * 50
            const localZ = (Math.random() - 0.5) * 50

            const localPos = new THREE.Vector3(localX, localY, localZ)
            localPos.applyMatrix4(rotationMatrix)

            positions[i * 3] = localPos.x + SNOW_CONFIG.cubeCenterX
            positions[i * 3 + 1] = localPos.y + SNOW_CONFIG.cubeCenterY
            positions[i * 3 + 2] = localPos.z + SNOW_CONFIG.cubeCenterZ

            const speed =
                SNOW_CONFIG.moveSpeed +
                (Math.random() - 0.5) * SNOW_CONFIG.speedVariation
            particleSpeedsRef.current.push(Math.max(0.1, speed))
        }

        geo.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3)
        )
        return geo
    }, [])

    // Animation Loop
    useFrame((state, delta) => {
        if (!meshRef.current) return

        const clampedDelta = Math.min(delta, 0.1)
        const time = state.clock.getElapsedTime()

        // 1. Update Projection Scale
        if (material.uniforms.uProjectionScale) {
            const dpr = Math.min(gl.getPixelRatio(), 2)
            const viewportHeightPx = size.height * dpr
            const fovRad = THREE.MathUtils.degToRad(camera.fov * 0.5)
            material.uniforms.uProjectionScale.value =
                viewportHeightPx / (2 * Math.tan(fovRad))
        }

        // 2. Calculate Fall Vectors
        const fallDirection = new THREE.Vector3(0, -1, 0)
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
            new THREE.Euler(
                SNOW_CONFIG.cubeRotationX,
                SNOW_CONFIG.cubeRotationY,
                SNOW_CONFIG.cubeRotationZ
            )
        )
        fallDirection.applyMatrix4(rotationMatrix)

        const inverseRotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
            new THREE.Euler(
                -SNOW_CONFIG.cubeRotationX,
                -SNOW_CONFIG.cubeRotationY,
                -SNOW_CONFIG.cubeRotationZ
            )
        )

        // 3. Update Positions
        const positions = meshRef.current.geometry.attributes.position.array
        const centerOffset = new THREE.Vector3(
            SNOW_CONFIG.cubeCenterX,
            SNOW_CONFIG.cubeCenterY,
            SNOW_CONFIG.cubeCenterZ
        )

        for (let i = 0; i < SNOW_CONFIG.particleCount; i++) {
            const i3 = i * 3
            const speed = particleSpeedsRef.current[i] || SNOW_CONFIG.moveSpeed

            // Move
            positions[i3] += fallDirection.x * speed * clampedDelta
            positions[i3 + 1] += fallDirection.y * speed * clampedDelta
            positions[i3 + 2] += fallDirection.z * speed * clampedDelta

            // Wiggle
            const wiggleX =
                Math.sin(time * SNOW_CONFIG.wiggleSpeed + i * 0.4) *
                SNOW_CONFIG.wiggleIntensity *
                clampedDelta
            const wiggleZ =
                Math.cos(time * SNOW_CONFIG.wiggleSpeed * 1.3 + i * 0.15) *
                SNOW_CONFIG.wiggleIntensity *
                clampedDelta

            positions[i3] += wiggleX
            positions[i3 + 2] += wiggleZ

            // Wrap Check
            const worldPos = new THREE.Vector3(
                positions[i3],
                positions[i3 + 1],
                positions[i3 + 2]
            )
            const localPos = worldPos.sub(centerOffset)
            localPos.applyMatrix4(inverseRotationMatrix)

            if (localPos.y < -25) {
                // Reset to top
                const newLocalX = (Math.random() - 0.5) * 50
                const newLocalY = 25
                const newLocalZ = (Math.random() - 0.5) * 50

                const newPos = new THREE.Vector3(
                    newLocalX,
                    newLocalY,
                    newLocalZ
                )
                newPos.applyMatrix4(rotationMatrix)

                positions[i3] = newPos.x + SNOW_CONFIG.cubeCenterX
                positions[i3 + 1] = newPos.y + SNOW_CONFIG.cubeCenterY
                positions[i3 + 2] = newPos.z + SNOW_CONFIG.cubeCenterZ

                // New Speed
                const newSpeed =
                    SNOW_CONFIG.moveSpeed +
                    (Math.random() - 0.5) * SNOW_CONFIG.speedVariation
                particleSpeedsRef.current[i] = Math.max(0.1, newSpeed)
            }
        }

        meshRef.current.geometry.attributes.position.needsUpdate = true
    })

    return (
        <points ref={meshRef} material={material} position={[0, -10, 0]}>
            <primitive object={geometry} />
        </points>
    )
}
